const lilyFormatMoney = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency
}).format(cents / 100);

const lilyElement = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
};

const lilyCartPromotionVariants = () => {
  const source = document.querySelector('[data-lily-cart-promotions]');
  if (!source) return new Map();

  try {
    const product = JSON.parse(source.textContent);
    return new Map(product.variants.map((variant) => [String(variant.id), variant]));
  } catch (_error) {
    return new Map();
  }
};

window.LilyeveCart = {
  async add(variantId) {
    const route = document.querySelector('[data-cart-add-url]')?.dataset.cartAddUrl
      || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const response = await fetch(route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to add this package. Please try again.');
    }

    const cart = await this.refresh();
    document.getElementById('cart-drawer')?.open();
    return cart;
  },

  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh your cart.');
    const cart = await response.json();
    this.render(cart);
    document.dispatchEvent(new CustomEvent('lilyeve:cart-updated', { detail: { cart } }));
    return cart;
  },

  async remove(lineKey) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'Unable to remove this item. Please try again.');
    }

    const cart = await response.json();
    this.render(cart);
    document.dispatchEvent(new CustomEvent('lilyeve:cart-updated', { detail: { cart } }));
    return cart;
  },

  render(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });

    const container = document.querySelector('[data-cart-drawer-content]');
    if (!container) return;
    container.replaceChildren();

    if (!cart.items.length) {
      const empty = lilyElement('div', 'lily-empty-cart');
      empty.append(lilyElement('p', '', 'Your cart is currently empty.'));
      const link = lilyElement('a', 'lily-button', 'Shop Grow:Turn');
      link.href = '#product';
      empty.append(link);
      container.append(empty);
      return;
    }

    const list = lilyElement('ul', 'lily-cart-items');
    list.setAttribute('role', 'list');
    const promotionVariants = lilyCartPromotionVariants();
    let promotionOriginalTotal = 0;
    cart.items.forEach((item) => {
      const promotion = promotionVariants.get(String(item.variant_id));
      const compareAtPrice = Math.max(Number(promotion?.compareAtPrice) || 0, item.original_price || item.price || 0);
      const originalLinePrice = Math.max(compareAtPrice * item.quantity, item.original_line_price || 0);
      const lineSaving = Math.max(0, originalLinePrice - item.final_line_price);
      promotionOriginalTotal += originalLinePrice;
      const line = lilyElement('li');
      if (item.image) {
        const image = lilyElement('img');
        image.src = item.image;
        image.width = 80;
        image.height = 80;
        image.alt = '';
        line.append(image);
      }
      const details = lilyElement('div', 'lily-cart-item-copy');
      details.append(lilyElement('strong', '', item.product_title));
      if (item.variant_title) details.append(lilyElement('p', 'lily-cart-variant', item.variant_title));
      const priceRow = lilyElement('div', 'lily-cart-price-row');
      priceRow.append(lilyElement('strong', '', lilyFormatMoney(item.final_line_price, cart.currency)));
      if (lineSaving > 0) priceRow.append(lilyElement('s', '', lilyFormatMoney(originalLinePrice, cart.currency)));
      details.append(priceRow);
      if (lineSaving > 0) {
        details.append(lilyElement('small', 'lily-cart-saving', `You save ${lilyFormatMoney(lineSaving, cart.currency)}`));
      }
      line.append(details);
      const removeButton = lilyElement('button', 'lily-cart-remove', 'Remove');
      removeButton.type = 'button';
      removeButton.dataset.cartRemove = '';
      removeButton.dataset.lineKey = item.key;
      removeButton.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
      line.append(removeButton);
      list.append(line);
    });

    const promotionSavingTotal = Math.max(0, promotionOriginalTotal - cart.total_price);
    const savings = lilyElement('div', 'lily-cart-savings-total');
    savings.append(lilyElement('span', '', 'Total savings'));
    savings.append(lilyElement('strong', '', lilyFormatMoney(promotionSavingTotal, cart.currency)));
    const total = lilyElement('div', 'lily-cart-total');
    total.append(lilyElement('span', '', 'Subtotal'));
    const totalPrices = lilyElement('div', 'lily-cart-total-prices');
    if (promotionSavingTotal > 0) {
      totalPrices.append(lilyElement('s', '', lilyFormatMoney(promotionOriginalTotal, cart.currency)));
    }
    totalPrices.append(lilyElement('strong', '', lilyFormatMoney(cart.total_price, cart.currency)));
    total.append(totalPrices);
    const checkout = lilyElement('a', 'lily-button lily-cart-checkout', 'Checkout');
    checkout.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    container.append(list);
    const status = lilyElement('p', 'lily-cart-status');
    status.dataset.cartStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    container.append(status);
    if (promotionSavingTotal > 0) container.append(savings);
    container.append(total, checkout);
  }
};

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-cart-remove]');
  if (!button || button.disabled) return;

  const label = button.textContent;
  const status = button.closest('[data-cart-drawer-content]')?.querySelector('[data-cart-status]');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Removing...';
  if (status) status.textContent = '';

  try {
    await window.LilyeveCart.remove(button.dataset.lineKey);
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = label;
    if (status) status.textContent = error.message || 'Unable to remove this item. Please try again.';
  }
});

window.LilyeveProduct = class LilyeveProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('[data-product-form]');
    this.button = section.querySelector('[data-product-submit]');
    this.status = section.querySelector('[data-product-status]');
    this.json = section.querySelector('[data-lily-product-json]');
    if (!this.form || !this.button || !this.json) return;

    this.product = JSON.parse(this.json.textContent);
    this.labels = {
      adding: this.form.dataset.addingLabel,
      error: this.form.dataset.errorLabel,
      soldOut: this.form.dataset.soldOutLabel
    };
    this.currency = this.product.currency || 'USD';
    this.radios = [...section.querySelectorAll('[data-package-radio]')];
    this.radios.forEach((radio) => radio.addEventListener('change', () => this.select(radio.value)));
    this.form.addEventListener('submit', this.onSubmit);

    const preferred = this.product.variants.find((variant) => variant.title === '2 Bottles' && variant.available)
      || this.product.variants.find((variant) => variant.available)
      || this.product.variants[0];
    if (preferred) this.select(String(preferred.id));
  }

  select(variantId) {
    const variant = this.product.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    this.variant = variant;
    this.radios.forEach((radio) => {
      const selected = radio.value === String(variant.id);
      radio.checked = selected;
      radio.closest('.lily-package')?.classList.toggle('is-selected', selected);
    });
    this.button.value = variant.id;
    this.button.disabled = !variant.available;
    this.button.querySelector('[data-button-label]').textContent = variant.available ? 'BUY NOW' : this.labels.soldOut;
  }

  onSubmit = async (event) => {
    if (!this.variant?.available) return;
    event.preventDefault();
    this.button.disabled = true;
    this.button.querySelector('[data-button-label]').textContent = this.labels.adding;
    this.status.textContent = '';
    try {
      await window.LilyeveCart.add(this.variant.id);
      this.status.textContent = `${this.variant.title} added to your cart.`;
    } catch (error) {
      this.status.textContent = error.message || this.labels.error;
    } finally {
      this.button.disabled = !this.variant.available;
      this.button.querySelector('[data-button-label]').textContent = this.variant.available ? 'BUY NOW' : this.labels.soldOut;
    }
  };
};

document.querySelectorAll('[data-lily-product]').forEach((section) => new window.LilyeveProduct(section));

class LilyProductZoom {
  constructor(modal) {
    this.modal = modal;
    this.section = document.getElementById(modal.dataset.productSection);
    this.select = modal.querySelector('[data-zoom-package]');
    this.button = modal.querySelector('[data-zoom-buy]');
    this.status = modal.querySelector('[data-zoom-status]');
    if (!this.section || !this.select || !this.button || this.select.disabled) return;

    this.select.addEventListener('change', this.syncPackage);
    this.button.addEventListener('click', this.addPackage);
    this.modal.addEventListener('lilyeve:modal-opened', this.syncFromProduct);
    this.syncFromProduct();
  }

  syncFromProduct = () => {
    const selected = this.section.querySelector('[data-package-radio]:checked');
    if (selected) this.select.value = selected.value;
    this.updateButton();
  };

  syncPackage = () => {
    const radio = [...this.section.querySelectorAll('[data-package-radio]')]
      .find((item) => item.value === this.select.value);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.updateButton();
  };

  updateButton() {
    this.button.disabled = this.select.selectedOptions[0]?.disabled ?? true;
  }

  addPackage = async () => {
    if (this.button.disabled) return;
    this.button.disabled = true;
    this.button.textContent = 'ADDING...';
    this.status.textContent = '';
    try {
      await window.LilyeveCart.add(this.select.value);
      this.modal.close();
    } catch (error) {
      this.status.textContent = error.message || 'Unable to add this package. Please try again.';
    } finally {
      this.button.textContent = 'BUY NOW';
      this.updateButton();
    }
  };
}

document.querySelectorAll('[data-product-zoom]').forEach((modal) => new LilyProductZoom(modal));

document.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
  button.addEventListener('click', () => {
    const section = button.closest('[data-lily-product]');
    const mainImage = section?.querySelector('[data-gallery-main]');
    if (!mainImage) return;
    const zoomImage = document.querySelector('[data-gallery-zoom]');
    mainImage.src = button.dataset.gallerySrc;
    mainImage.alt = button.dataset.galleryAlt;
    if (zoomImage) {
      zoomImage.src = button.dataset.gallerySrc;
      zoomImage.alt = button.dataset.galleryAlt;
    }
    section.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb === button);
      thumb.setAttribute('aria-pressed', String(thumb === button));
    });
  });
});
